import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import TaskV2 from './TaskV2'
import TaskModal from '../../ChatModals/TaskModal'
import ErrorToast from '../../ErrorToast'
import ShowMoreLess from '../../../atoms/ShowMoreLess'

import { AddErrorAlert } from '@SonarCX/slices/alert'
import { fetchStatus } from '@SonarCX/slices/taskStatus'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import { useTaskList } from '@SonarCX/providers/TaskListProvider'
import { useChatScroll } from '@SonarCX/providers/ChatScrollProvider'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'
import useStyles from './styles'

const formatTaskForModal = task => ({
  ...task,
  date: task.due_date,
  section: task.category_id,
  client: task.concerning_user_id,
  primary: task.assignee_ids,
  watchers: task.watcher_ids,
  type: task.type_id,
  status: task.status_id,
  priority: task.priority_id,
  tags: task.tags ?? [],
  content: task.details
})

const TasksV2 = ({ user, allUsers }) => {
  const dispatch = useAppDispatch()

  const {
    statuses,
    loaded: statusesLoaded,
    loading,
    error: statusesError
  } = useAppSelector(storeState => storeState.taskStatus)

  const {
    tasks: allTasks,
    loadTask,
    loaded: tasksLoaded,
    fetchSubtasks,
    saveTaskMedia,
    updateTask,
    updateFetchedSubtasks
  } = useTaskAPI()
  const { setTasks: setOwnerTasks } = useOwner()
  const { id: sessionID = null } = useQueueState()
  const { reset: resetTasks } = useTaskList()
  const { scrollToBottom } = useChatScroll()

  const [tasks, _setTasks] = React.useState([])
  const [session, setSession] = React.useState(null)
  const [media, setMedia] = React.useState([])
  const [modalOpen, setModalOpen] = React.useState(false)
  const [fullTask, setFullTask] = React.useState(null)
  const [taskOpen, setTaskOpen] = React.useState(null)
  const [subtasks, setSubtasks] = React.useState(null)
  const [loaded, setLoaded] = React.useState(null)
  const [error, setError] = React.useState(null)
  const [expanded, setExpanded] = React.useState(false)

  // Intermediary setter for tasks, places them in the correct order
  const setTasks = React.useCallback(
    tasks => {
      // Split tasks like timeline does
      const previous = tasks.filter(
        t => !(t.session_ids ?? []).includes(sessionID)
      )
      const current = tasks.filter(t =>
        (t.session_ids ?? []).includes(sessionID)
      )

      // Sort by created date
      const taskSort = (a, b) => new Date(a.created_at) - new Date(b.created_at)
      const previousSorted = previous.sort(taskSort)
      const currentSorted = current.sort(taskSort)

      // Combine and store
      const combined = [...previousSorted, ...currentSorted]
      _setTasks(combined)
      setOwnerTasks(combined.map(t => t.id))
    },
    [setOwnerTasks, sessionID]
  )

  const taskIDs = React.useMemo(() => {
    if (!user || !user.tasks) {
      return []
    }

    return user.tasks.map(task => task.id)
  }, [user])

  const task = React.useMemo(
    () => tasks.find(t => t?.id === taskOpen) ?? {},
    [tasks, taskOpen]
  )

  const {
    canUpdate,
    canEdit,
    canDelete,
    canClose,
    canView: canViewTask
  } = useTaskPermissions(task)

  const classes = useStyles()

  const lookForTask = React.useCallback(
    task => {
      let cont = false
      if (taskIDs.includes(task.id) || task.concerning_user_id === user.id) {
        cont = true
      } else {
        return false
      }

      if (!task.deleted_at) {
        cont = true
      } else {
        return false
      }

      if (!task.closed_at) {
        return cont
      }

      return false
    },
    [taskIDs, user]
  )

  // Get tasks for this user, also responds to task updates from modal or timeline
  React.useEffect(() => {
    if (!user) {
      setTasks([])
      return
    }

    const tasks = []
    const foundIDs = []
    Object.values(allTasks).forEach(task => {
      const taskFound = lookForTask(task)

      if (taskFound && !foundIDs.includes(task.id)) {
        const dict = allTasks[task.id]

        // dont load tasks with deleted or closed date
        if (!dict.deleted_at && !dict.closed_at) {
          tasks.push(task)
          foundIDs.push(task.id)
        }
      }
    })

    // Set found tasks
    setTasks(tasks)

    setTimeout(() => {
      scrollToBottom()
    }, 200)
  }, [allTasks, user, lookForTask, setTasks, scrollToBottom])

  // gets the full task when opened
  React.useEffect(() => {
    if (!tasksLoaded) {
      return
    }

    if (taskOpen) {
      const foundTask = allTasks[taskOpen]

      if (foundTask) {
        // reset subtasks to be fetched again
        setSubtasks(null)
        if (Object.keys(foundTask).length > 2) {
          const formatted = formatTaskForModal(foundTask)
          setFullTask(formatted)

          // sets session only if it's the parent
          if (!foundTask.parent_id) {
            setSession(foundTask.session_ids[0] ?? null)
          }
        }
      } else {
        loadTask(taskOpen)
      }
    }
  }, [taskOpen, tasks, tasksLoaded, allTasks, loadTask])

  // Gets subtasks for that task
  React.useEffect(() => {
    if (!fullTask || subtasks) {
      return
    }

    let mounted = true
    // calling fetch if modal is open && only if subtasksLoaded is false
    if (modalOpen && fullTask.sub_tasks !== 0 && fullTask.id) {
      if (mounted) {
        fetchSubtasks(fullTask.id)
          .then(subs => {
            setSubtasks(subs)
          })
          .catch(err => setError({ err, auto: true }))
      }
    }
    return () => {
      mounted = false
    }
  }, [
    fullTask,
    subtasks,
    fetchSubtasks,
    modalOpen,
    taskOpen,
    allTasks,
    updateFetchedSubtasks
  ])

  // starting to call media if media_id exists
  React.useEffect(() => {
    if (fullTask?.media_ids.length && !fullTask?.media) {
      saveTaskMedia(fullTask.id)
      setLoaded(true)
    }

    if (loaded && fullTask?.media) {
      setMedia(fullTask.media)
    }
  }, [allTasks, loaded, saveTaskMedia, fullTask])

  const handleSubmit = React.useCallback(
    async newTask => {
      try {
        await updateTask(fullTask.id, newTask)

        const formatted = formatTaskForModal(newTask)

        formatted.id = fullTask.id

        setFullTask(formatTaskForModal(newTask))

        // search and replace tasks to update title and content
        const oldTasks = [...tasks]
        const ids = oldTasks.map(t => t.id)
        const idx = ids.indexOf(fullTask.id)

        oldTasks.splice(idx, 1)
        oldTasks.push(formatted)

        setTasks(oldTasks)
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
      }
    },
    [updateTask, fullTask, tasks, setTasks, dispatch]
  )

  // Fetch subtasks of this task if it wasn't loaded before only if modal is open
  React.useEffect(() => {
    if (!modalOpen) {
      return
    }

    let mounted = true

    const id = taskOpen ?? task.id

    fetchSubtasks(id)
      .then(subs => {
        mounted && setSubtasks(subs)
      })
      .catch(err => setError({ err, auto: true }))

    return () => {
      mounted = false
    }
  }, [task.id, modalOpen, taskOpen, fetchSubtasks])

  React.useEffect(() => {
    if (!statusesLoaded && !loading) {
      dispatch(fetchStatus())
    }
  }, [statusesLoaded, loading, dispatch])

  const tasksToRender = expanded ? [...tasks] : [...tasks].slice(0, 3)

  return (
    <div className={classes.root}>
      {tasks?.length === 0 && (
        <div className={classes.noTasks}>No tasks found</div>
      )}
      <React.Fragment>
        {tasksToRender.map(task => (
          <TaskV2
            key={task.id}
            task={task}
            setTaskOpen={setTaskOpen}
            setModalOpen={setModalOpen}
            allUsers={allUsers}
            statuses={statuses}
            canEdit={canEdit}
          />
        ))}
        {tasks.length > 3 && (
          <ShowMoreLess expanded={expanded} setExpanded={setExpanded} />
        )}
      </React.Fragment>

      {modalOpen && (
        <TaskModal
          open={modalOpen}
          onClose={() => {
            resetTasks()
            setModalOpen(false)
            setTaskOpen(null)
          }}
          session={session}
          superVals={fullTask}
          onTaskCreated={handleSubmit}
          subtasks={subtasks}
          taskId={taskOpen}
          disableTaskCreation
          updateSuperVals={() => {}}
          setTaskOpen={setTaskOpen}
          media={media}
          task={task}
          user={user ?? null}
          canEdit={canEdit}
          canUpdate={canUpdate}
          canDelete={canDelete}
          canViewTask={canViewTask}
          canClose={canClose}
        />
      )}

      <ErrorToast error={error || statusesError} v2 />
    </div>
  )
}

export default TasksV2
